import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import ImageUploader from 'components/ImageUploader/ImageUploader';
import Panel from 'components/Panel/Panel';
import useDeleteBranding from 'hooks/useDeleteBranding';
import useUpdateBranding, { AssetType } from 'hooks/useUpdateBranding';
import { t } from 'locales/i18n';

interface OverviewAssessmentBrandingProps {
  isLoading: boolean;
  coverImage?: string;
  coverLogo?: string;
  emailLogo?: string;
  interviewUid?: string;
  isDefaultBranding: boolean;
  assessmentName: string;
}

const OverviewAssessmentBranding = ({
  isLoading,
  coverImage,
  coverLogo,
  emailLogo,
  interviewUid,
  isDefaultBranding,
  assessmentName,
}: OverviewAssessmentBrandingProps) => {
  const { deleteBranding, isLoading: isDeleteLoading } = useDeleteBranding();
  const { updateBranding, isLoading: isUpdateLoading } = useUpdateBranding();
  const queryClient = useQueryClient();

  const [coverImageUrl, setCoverImageUrl] = useState<string | undefined>(
    undefined
  );
  const [coverLogoUrl, setCoverLogoUrl] = useState<string | undefined>(
    undefined
  );
  const [emailLogoUrl, setEmailLogoUrl] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    setCoverImageUrl(coverImage);
  }, [coverImage]);

  useEffect(() => {
    setCoverLogoUrl(coverLogo);
  }, [coverLogo]);

  useEffect(() => {
    setEmailLogoUrl(emailLogo);
  }, [emailLogo]);

  const handleDeleteBrandingAsset = async (type: AssetType) => {
    if (!interviewUid) {
      return;
    }

    const response = await deleteBranding(
      {
        interviewUid,
        type,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['getAssessmentDetails', interviewUid],
          });
        },
      }
    );

    if (response.interview) {
      if (type === AssetType.COVER_PHOTO) {
        setCoverImageUrl(undefined);
      } else if (type === AssetType.COVER_LOGO) {
        setCoverLogoUrl(undefined);
      } else if (type === AssetType.EMAIL_LOGO) {
        setEmailLogoUrl(undefined);
      }
    }
  };

  const handleUpdateBrandingAsset = async (type: AssetType, image: Blob) => {
    if (!interviewUid) {
      return;
    }

    const response = await updateBranding(
      {
        interviewUid,
        type,
        asset: image,
        name: `${assessmentName}-${interviewUid}-${Date.now()}`,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['getAssessmentDetails', interviewUid],
          });
        },
      }
    );

    if (response.cover_photo_url) {
      setCoverImageUrl(response.cover_photo_url);
    }
    if (response.dark_background_logo_url) {
      setCoverLogoUrl(response.dark_background_logo_url);
    }
    if (response.light_background_logo_url) {
      setEmailLogoUrl(response.light_background_logo_url);
    }
  };

  const handleDeleteCoverImage = async () => {
    handleDeleteBrandingAsset(AssetType.COVER_PHOTO);
  };

  const handleUploadCoverImage = async (image: Blob) => {
    handleUpdateBrandingAsset(AssetType.COVER_PHOTO, image);
  };

  const handleDeleteCoverLogo = async () => {
    handleDeleteBrandingAsset(AssetType.COVER_LOGO);
  };

  const handleUploadCoverLogo = async (image: Blob) => {
    handleUpdateBrandingAsset(AssetType.COVER_LOGO, image);
  };

  const handleDeleteEmailLogo = async () => {
    handleDeleteBrandingAsset(AssetType.EMAIL_LOGO);
  };

  const handleUploadEmailLogo = async (image: Blob) => {
    handleUpdateBrandingAsset(AssetType.EMAIL_LOGO, image);
  };

  return (
    <Panel title={t('Branding')}>
      <div className="flex flex-col lg:flex-row" data-testid="branding-section">
        {isLoading || isUpdateLoading || isDeleteLoading ? (
          <Skeleton height={200} />
        ) : (
          <ImageUploader
            image={coverImageUrl}
            onDelete={handleDeleteCoverImage}
            onEdit={handleUploadCoverImage}
            labelText={t('Cover Photo')}
            tooltipText={t(
              'Use a cover photo to showcase your school or campus to applicants during check-in. We recommend using high-quality image files.'
            )}
            id={`file-uploader-${AssetType.COVER_PHOTO}`}
            isDefaultBranding={isDefaultBranding}
            useImageAsBackground
            sectionDataTestId="cover-photo-section"
          />
        )}
        <div className="hidden w-6 lg:block" />
        <div className="mt-6 flex flex-1 lg:mt-0">
          <div className="relative flex-1">
            {isLoading || isUpdateLoading || isDeleteLoading ? (
              <Skeleton height={200} />
            ) : (
              <ImageUploader
                image={coverLogoUrl}
                onDelete={handleDeleteCoverLogo}
                onEdit={handleUploadCoverLogo}
                labelText={t('Light Logo')}
                tooltipText={t(
                  "We recommend a logo that will be visible on darker backgrounds, like your assessment's Check In page. Ideally this logo is all white with a transparent background."
                )}
                containerClassName="flex items-center justify-center"
                id={`file-uploader-${AssetType.COVER_LOGO}`}
                isDefaultBranding={isDefaultBranding}
                useDarkContrast
                sectionDataTestId="light-logo-section"
              />
            )}
          </div>
          <div className="w-6" />
          <div className="relative flex-1">
            {isLoading || isUpdateLoading || isDeleteLoading ? (
              <Skeleton height={200} />
            ) : (
              <ImageUploader
                image={emailLogoUrl}
                onDelete={handleDeleteEmailLogo}
                onEdit={handleUploadEmailLogo}
                labelText={t('Dark Logo')}
                tooltipText={t(
                  'We recommend a logo that will be visible on lighter backgrounds, like the emails applicants receive from Kira. Ideally this logo is colourful with a transparent background.'
                )}
                containerClassName="flex items-center justify-center"
                id={`file-uploader-${AssetType.EMAIL_LOGO}`}
                isDefaultBranding={isDefaultBranding}
                sectionDataTestId="dark-logo-section"
              />
            )}
          </div>
        </div>
      </div>
    </Panel>
  );
};

export default OverviewAssessmentBranding;
